/**
 * @fileoverview High-level game object factory for creating Konva shapes.
 * 
 * **Role**: Creates complete game objects (draggables, targets, UI) using themes.
 * 
 * **Separation of Concerns**:
 * - `ShapeHelpers` (shape-helpers.ts): Low-level Konva primitives (circles, text, etc.)
 * - `ShapeFactory` (this file): High-level game objects that use themes + ShapeHelpers
 * 
 * **Responsibility**:
 * - Creates draggable shapes with event handlers
 * - Creates target shapes (ghost outlines)
 * - Creates UI elements (feedback text)
 * - Coordinates with BaseTheme for shape creation and styling
 * 
 * **Usage Pattern**:
 * ```typescript
 * // Create all game shapes for current theme
 * const targets = ShapeFactory.createTargets(matchingPairs, layer, theme);
 * const draggables = ShapeFactory.createDraggables(matchingPairs, layer, handlers, theme);
 * const feedbackText = ShapeFactory.createFeedbackText(layer);
 * ```
 * 
 * All methods are static factory methods that create fully configured game objects.
 */

import Konva from 'konva';
import { CONFIG } from '../core/GameConfig.js';
import { ColorUtils } from '../utilities/ColorUtils.js';
import { DragHandlers } from '../core/DragHandlers.js';
import { MatchingPair } from '../utilities/GameUtils.js';
import { CustomNode, KonvaEventAbstract } from '../konva-extensions.js';
import { BaseTheme } from '../themes/abstract/BaseTheme.js';

/**
 * Factory class for creating high-level game objects.
 * Uses themes to create shapes with appropriate styling and behavior.
 */
export class ShapeFactory {
    /**
     * Create all target shapes (ghost outlines) for matching.
     * @param matchingPairs - Array of matching pair configurations
     * @param staticLayer - Konva layer to add targets to
     * @param theme - Current theme for shape creation and styling
     * @returns Array of target CustomNodes
     */
    static createTargets(matchingPairs: MatchingPair[], staticLayer: Konva.Layer, theme: BaseTheme): CustomNode[] {
        const numPairs = matchingPairs.length;
        const positions = theme.getPositions(CONFIG.WIDTH, CONFIG.HEIGHT, matchingPairs, CONFIG.SHAPE_SIZE);
        const targets: CustomNode[] = [];
        
        for (let i = 0; i < numPairs; i++) {
            const pair = matchingPairs[i];
            const tsIndex = pair.tsIndex;
            const pos = positions.TS_POSITIONS[tsIndex];
            
            // Create lighter version of the matching color
            const matchingColor = ColorUtils.lightenColor(pair.color.fill, 0.5);
            const matchingStroke = ColorUtils.lightenColor(pair.color.stroke, 0.15);
            
            const shapeType = theme.getShapeTypeForPair(pair);
            const label = theme.getLabelForPair(pair);
            
            const target = theme.createShape(
                pos.x, 
                pos.y, 
                CONFIG.SHAPE_SIZE * 1.1,
                { fill: matchingColor, stroke: matchingStroke },
                false,
                label || shapeType
            ) as CustomNode;
            
            // Apply theme-specific styling
            theme.styleTarget(target);
            
            target.tsIndex = tsIndex;
            
            // For sequential themes, hide all targets except the one with tsIndex 0
            // (tsIndex determines the order they appear, not array position)
            if (theme.isSequential && tsIndex !== 0) {
                target.visible(false);
                target.isHidden = true;
                console.log(`[SequentialTheme] Hiding target tsIndex=${tsIndex}, visible=${target.visible()}`);
            } else if (theme.isSequential) {
                console.log(`[SequentialTheme] Showing target tsIndex=${tsIndex}, visible=${target.visible()}`);
            }
            
            targets.push(target);
            staticLayer.add(target);
        }
        
        return targets;
    }
    
    /**
     * Create all draggable shapes with event handlers.
     */
    static createDraggables(matchingPairs: MatchingPair[], staticLayer: Konva.Layer, dragHandlers: DragHandlers, theme: BaseTheme): CustomNode[] {
        const numPairs = matchingPairs.length;
        const positions = theme.getPositions(CONFIG.WIDTH, CONFIG.HEIGHT, matchingPairs, CONFIG.SHAPE_SIZE);
        const draggables = [];
        
        for (let i = 0; i < numPairs; i++) {
            const pair = matchingPairs[i];
            const pos = positions.DS_POSITIONS[i];
            const draggableSize = CONFIG.SHAPE_SIZE * 1.0;
            
            const shapeType = theme.getShapeTypeForPair(pair);
            const label = theme.getLabelForPair(pair);
            
            const draggable = theme.createShape(
                pos.x,
                pos.y,
                draggableSize,
                pair.color,
                true,
                label || shapeType
            ) as CustomNode;
            
            draggable.draggable(true);
            draggable.dragDistance(0);
            draggable.shapeColor = pair.color;
            
            // Apply theme-specific styling
            theme.styleDraggable(draggable);
            
            // Store references for later
            draggable.dsIndex = i;
            draggable.originalPos = { x: pos.x, y: pos.y };
            draggable.correctTsIndex = pair.tsIndex;
            draggable.shapeType = shapeType; // Store for matching in SHAPES theme
            
            draggables.push(draggable);
            staticLayer.add(draggable);
            
            // Add immediate selection feedback on touch/mousedown
            draggable.on('mousedown touchstart', function(evt: KonvaEventAbstract) {
                dragHandlers.showSelectionFeedback(draggable, evt);
            });
            
            // Remove feedback on release (in case drag didn't start)
            draggable.on('mouseup touchend', function() {
                dragHandlers.removeSelectionFeedback(draggable);
            });
            
            // Setup drag handlers (pass the Konva event to dragstart so we can
            // unlock audio from a trusted user gesture)
            draggable.on('dragstart', function(evt: any) { dragHandlers.onDragStart(draggable, evt); });
            draggable.on('dragend', function() { dragHandlers.onDragEnd(draggable); });
            draggable.on('dragmove', function() { dragHandlers.onDragMove(draggable); });
        }
        
        return draggables;
    }
    
    /**
     * Create the title text at the top of the canvas.
     * @param {Konva.Layer} staticLayer - Konva layer to add text to
     */
    static createTitle(staticLayer: Konva.Layer) {
        const title = new Konva.Text({
            x: CONFIG.WIDTH / 2,
            y: CONFIG.TITLE_Y_POSITION,
            text: CONFIG.TITLE_TEXT,
            fontSize: CONFIG.TITLE_FONT_SIZE,
            fontFamily: 'Arial',
            fill: CONFIG.TITLE_COLOR,
            align: 'center',
            offsetX: 0
        });
        title.offsetX(title.width() / 2);
        staticLayer.add(title);
    }
    
    /**
     * Create the feedback text (hidden initially) for success messages.
     * @param {Konva.Layer} staticLayer - Konva layer to add text to
     * @returns {Konva.Text} Konva.Text feedback object
     */
    static createFeedbackText(staticLayer: Konva.Layer): Konva.Text {
        const feedbackText = new Konva.Text({
            x: CONFIG.WIDTH / 2,
            y: CONFIG.FEEDBACK_Y_POSITION,
            text: '',
            fontSize: CONFIG.FEEDBACK_FONT_SIZE,
            fontFamily: 'Arial',
            fill: CONFIG.SUCCESS_COLOR,
            fontStyle: 'bold',
            align: 'center',
            opacity: 0,
            zIndex: -1000

        });
        staticLayer.add(feedbackText);
        return feedbackText;
    }
}
